import React, { useState, useEffect } from 'react';
import { Button, Checkbox, message, Input, Avatar, Card, Tag, Tooltip, Empty } from 'antd';
import { FaEdit, FaThumbtack, FaTrash, FaPaperPlane, FaUser, FaClock } from 'react-icons/fa';
import { EyeInvisibleOutlined, EyeOutlined, MessageOutlined, PushpinOutlined } from '@ant-design/icons';
import setCommentsProcess from 'services/processes/setCommentsProcess';
import { lerComentarios } from 'utils/comentarios';
import { getUserCachedData } from "../../../../utils/UserAuthorization";
import { primaryColor } from 'assets/global';
import { cacheClear } from 'utils/memoryCache';

const { TextArea } = Input;

function SetCommentsProcess({ processId, successCallback, initialValue }) {
  const user = getUserCachedData() || {};
  const userFirstName = user?.first_name || '';
  const userLastName = user?.last_name || '';
  const userAvatar = `${userLastName[0] || ''}${userFirstName[0] || ''}`.toUpperCase() || 'U';

  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState({
    avatar: userAvatar,
    ResponsavelId: user?.id,
    ResponsavelNome: `${userFirstName} ${userLastName}`.trim(),
    comentario: ''
  });
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingText, setEditingText] = useState('');

  useEffect(() => {
    setComments(lerComentarios(initialValue));
  }, [initialValue]);

  const handleAddComment = async () => {
    if (comment.comentario.trim() !== '') {
      const newComment = { ...comment, data: new Date() };
      const updatedComments = [...comments, newComment];
      setComments(updatedComments);
      
      // Auto-salvar no processo quando adicionar comentário
      if (processId) {
        try {
          await setCommentsProcess(processId, updatedComments);
          cacheClear('processes:');
          if (successCallback) successCallback();
        } catch (err) {
          message.error('Erro ao salvar comentário.');
        }
      } else {
        message.warning('Comentário adicionado localmente. Salve o processo para persistir.');
      }
      
      setComment({
        avatar: userAvatar,
        ResponsavelId: user?.id,
        ResponsavelNome: `${userFirstName} ${userLastName}`.trim(),
        comentario: ''
      });
    }
  };

  const handleEditComment = (originalIndex, currentComment) => {
    setEditingIndex(originalIndex);
    setEditingText(currentComment.comentario || '');
  };

  const handleSaveComment = async (originalIndex) => {
    if (originalIndex < 0 || originalIndex >= comments.length) return;
    const updatedComments = [...comments];
    updatedComments[originalIndex] = {
      ...updatedComments[originalIndex],
      comentario: editingText
    };
    setComments(updatedComments);
    setEditingIndex(-1);
    setEditingText('');
    
    // Auto-salvar no processo quando editar comentário
    if (processId) {
      try {
        await setCommentsProcess(processId, updatedComments);
        cacheClear('processes:');
        if (successCallback) successCallback();
      } catch (err) {
        message.error('Erro ao salvar comentário editado.');
      }
    }
  };

  const handlePinComment = async (originalIndex) => {
    if (originalIndex < 0 || originalIndex >= comments.length) return;
    const updatedComments = [...comments];
    updatedComments[originalIndex] = {
      ...updatedComments[originalIndex],
      fixed: !updatedComments[originalIndex].fixed
    };
    setComments(updatedComments);
    
    // Auto-salvar no processo quando fixar/desafixar comentário
    if (processId) {
      try {
        await setCommentsProcess(processId, updatedComments);
        cacheClear('processes:');
        if (successCallback) successCallback();
      } catch (err) {
        message.error('Erro ao salvar alteração de fixação.');
      }
    }
  };

  // Comentários são internos; "visível ao cliente" libera no portal do cliente
  const handleToggleCliente = async (originalIndex) => {
    if (originalIndex < 0 || originalIndex >= comments.length) return;
    const updatedComments = [...comments];
    const mostrar = !updatedComments[originalIndex].cliente;
    updatedComments[originalIndex] = { ...updatedComments[originalIndex], cliente: mostrar };
    setComments(updatedComments);
    if (processId) {
      try {
        await setCommentsProcess(processId, updatedComments);
        cacheClear('processes:');
        message.success(mostrar ? 'O cliente passa a ver este comentário no portal.' : 'Comentário oculto do cliente.');
        if (successCallback) successCallback();
      } catch (err) {
        message.error('Erro ao salvar a visibilidade do comentário.');
      }
    }
  };

  const handleDeleteComment = async (originalIndex) => {
    if (originalIndex < 0 || originalIndex >= comments.length) return;
    const updatedComments = comments.filter((_, i) => i !== originalIndex);
    setComments(updatedComments);
    
    // Auto-salvar no processo quando deletar comentário
    if (processId) {
      try {
        await setCommentsProcess(processId, updatedComments);
        cacheClear('processes:');
        if (successCallback) successCallback();
      } catch (err) {
        message.error('Erro ao salvar exclusão do comentário.');
      }
    }
  };

  // Ordenar comentários mantendo o índice original para ações corretas
  const sortedComments = comments
    .map((item, originalIndex) => ({ ...item, originalIndex }))
    .sort((a, b) => {
      if (a.fixed && !b.fixed) return -1;
      if (!a.fixed && b.fixed) return 1;
      return new Date(a.data) - new Date(b.data);
    });

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      background: 'var(--yrm-surface-2)',
      padding: '16px',
      borderRadius: '12px',
      height: '500px'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px',
        marginBottom: '12px',
        flexShrink: 0
      }}>
        <MessageOutlined style={{ color: 'var(--yrm-heading)', fontSize: '16px' }} />
        <h4 style={{ 
          margin: 0, 
          color: 'var(--yrm-text-2)', 
          fontSize: '16px', 
          fontWeight: 600 
        }}>
          Comentários do Processo
        </h4>
        <span style={{ 
          background: 'var(--yrm-primary)', 
          color: 'white', 
          borderRadius: '12px', 
          padding: '2px 8px', 
          fontSize: '12px',
          fontWeight: 600
        }}>
          {comments.length}
        </span>
      </div>

      {/* Lista de comentários com scroll */}
      <div style={{ 
        flex: 1,
        overflowY: 'auto',
        paddingRight: '4px',
        marginBottom: '12px'
      }}>
        {sortedComments.length === 0 ? (
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: 'var(--yrm-text-2)' }}>
                Nenhum comentário adicionado ainda
              </span>
            }
          />
        ) : (
          sortedComments.map((c) => (
            <Card
              key={c.originalIndex}
              size="small"
              style={{ 
                marginBottom: '12px',
                background: c.fixed ? 'var(--yrm-tint-azul)' : 'var(--yrm-surface)',
                border: c.fixed ? '2px solid #1890ff' : '1px solid var(--yrm-border)',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
              }}
              bodyStyle={{ padding: '12px' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <Avatar 
                  style={{ 
                    backgroundColor: primaryColor,
                    flexShrink: 0
                  }}
                  size="small"
                >
                  {c.avatar}
                </Avatar>
                
                <div style={{ flex: 1 }}>
                  {/* Header do comentário */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <div>
                      <span style={{ 
                        fontWeight: 600, 
                        color: 'var(--yrm-text-2)',
                        fontSize: '13px'
                      }}>
                        {c.ResponsavelNome}
                      </span>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '4px',
                        marginTop: '2px'
                      }}>
                        <FaClock style={{ fontSize: 12, color: '#718096' }} />
                        <span style={{ 
                          fontSize: 12, 
                          color: '#718096' 
                        }}>
                          {c.data ? new Date(c.data).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'sem data'}
                        </span>
                        {c.cliente && <Tag bordered={false} color='green' style={{ margin: '0 0 0 4px', fontSize: 11 }}>Visível ao cliente</Tag>}
                        {c.fixed && (
                          <PushpinOutlined style={{ 
                            fontSize: 12, 
                            color: 'var(--yrm-link)',
                            marginLeft: '4px'
                          }} />
                        )}
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <Tooltip title="Editar comentário">
                        <Button 
                          size="small"
                          type="text"
                          icon={<FaEdit />}
                          onClick={() => handleEditComment(c.originalIndex, c)}
                          style={{ 
                            color: '#3182ce',
                            fontSize: '12px'
                          }}
                        />
                      </Tooltip>
                      <Tooltip title={c.cliente ? 'Visível ao cliente no portal (clique para ocultar)' : 'Interno (clique para mostrar ao cliente no portal)'}>
                        <Button
                          size="small"
                          type="text"
                          icon={c.cliente ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                          onClick={() => handleToggleCliente(c.originalIndex)}
                          aria-label={c.cliente ? 'Ocultar do cliente' : 'Mostrar ao cliente'}
                          style={{ color: c.cliente ? 'var(--yrm-sucesso)' : 'var(--yrm-text-4)', fontSize: '12px' }}
                        />
                      </Tooltip>
                      <Tooltip title={c.fixed ? "Desafixar comentário" : "Fixar comentário"}>
                        <Button 
                          size="small"
                          type="text"
                          icon={<FaThumbtack />}
                          onClick={() => handlePinComment(c.originalIndex)}
                          style={{ 
                            color: c.fixed ? 'var(--yrm-link)' : '#718096',
                            fontSize: '12px'
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Excluir comentário">
                        <Button 
                          size="small"
                          type="text"
                          icon={<FaTrash />}
                          onClick={() => handleDeleteComment(c.originalIndex)}
                          style={{ 
                            color: '#e53e3e',
                            fontSize: '12px'
                          }}
                        />
                      </Tooltip>
                    </div>
                  </div>

                  {/* Conteúdo do comentário */}
                  {editingIndex === c.originalIndex ? (
                    <div>
                      <TextArea
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        placeholder="Editar seu comentário aqui"
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        style={{ 
                          width: '100%',
                          borderRadius: '6px',
                          marginBottom: '8px'
                        }}
                      />
                      <div style={{ textAlign: 'right', gap: '8px', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                          size="small"
                          onClick={() => {
                            setEditingIndex(-1);
                            setEditingText('');
                          }}
                        >
                          Cancelar
                        </Button>
                        <Button 
                          size="small"
                          type="primary" 
                          onClick={() => handleSaveComment(c.originalIndex)}
                        >
                          Salvar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p style={{ 
                      margin: 0, 
                      wordBreak: 'break-word',
                      lineHeight: 1.5,
                      color: 'var(--yrm-text-2)',
                      fontSize: '13px'
                    }}>
                      {c.comentario}
                    </p>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Input de novo comentário fixo na parte inferior */}
      <Card
        size="small"
        style={{ 
          background: 'var(--yrm-surface)',
          border: '2px solid var(--yrm-border)',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
          flexShrink: 0
        }}
        bodyStyle={{ padding: '12px' }}
      >
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <Avatar 
            style={{ 
              backgroundColor: primaryColor,
              flexShrink: 0
            }}
            size="small"
          >
            <FaUser />
          </Avatar>
          
          <div style={{ flex: 1 }}>
            <TextArea
              value={comment.comentario}
              onChange={(e) => setComment({ ...comment, comentario: e.target.value })}
              placeholder="Adicione um comentário..."
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{ 
                borderRadius: '6px',
                marginBottom: '8px'
              }}
            />
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center'
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--yrm-text-3)' }}>
                Comentando como {userFirstName} {userLastName}
                <Checkbox checked={Boolean(comment.cliente)} onChange={(e) => setComment({ ...comment, cliente: e.target.checked })}>
                  <span style={{ fontSize: 12 }}>Mostrar ao cliente no portal</span>
                </Checkbox>
              </span>
              
              <Button
                type="primary"
                icon={<FaPaperPlane />}
                onClick={handleAddComment}
                style={{ 
                  background: 'var(--yrm-primary)',
                  borderColor: 'var(--yrm-primary)',
                  borderRadius: '6px'
                }}
              >
                Comentar
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default SetCommentsProcess;
